import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import axios from 'axios';
import { api as ApiClient } from '../services/api';

// Function to ensure the CSRF token has been fetched
async function ensureCsrfToken() {
    // Check if the XSRF-TOKEN cookie exists
    const hasToken = document.cookie.includes('XSRF-TOKEN=');
    if (!hasToken) {
        try {
            await ApiClient.fetchCsrfToken();
        } catch (error) {
            console.error('[Echo] Failed to fetch CSRF token:', error);
        }
    }
}

declare global {
    interface Window {
        Pusher: typeof Pusher;
        Echo: Echo;
    }
}

window.Pusher = Pusher;

// Function to read a cookie value
const getCookieValue = (name: string): string | null => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
};

// Auth cache to reduce repeated authorization requests
interface AuthCacheEntry {
    authData: unknown;
    timestamp: number;
    expiresAt: number;
    sessionId?: string; // Track session for invalidation
}

class AuthCache {
    private cache = new Map<string, AuthCacheEntry>();
    private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes fallback
    private readonly SESSION_CHECK_INTERVAL = 30 * 1000; // Check session every 30 seconds
    private currentSessionId: string | null = null;
    private sessionCheckTimer: NodeJS.Timeout | null = null;

    constructor() {
        this.updateSessionId();
        this.startSessionMonitoring();
    }

    // Extract session ID from XSRF token or generate a simple session identifier
    private updateSessionId(): void {
        const xsrfToken = getCookieValue('XSRF-TOKEN');
        const sessionToken = getCookieValue('laravel_session');

        // Use XSRF token or session cookie as session identifier
        this.currentSessionId = xsrfToken || sessionToken || `session_${Date.now()}`;
    }

    // Monitor session changes and clear cache when session changes
    private startSessionMonitoring(): void {
        if (this.sessionCheckTimer) {
            clearInterval(this.sessionCheckTimer);
        }

        this.sessionCheckTimer = setInterval(() => {
            const previousSessionId = this.currentSessionId;
            this.updateSessionId();

            // If session changed, clear all cached auth data
            if (previousSessionId && this.currentSessionId !== previousSessionId) {
                this.clear();
            }
        }, this.SESSION_CHECK_INTERVAL);
    }

    // Calculate cache duration based on Laravel session lifetime (default 2 hours = 120 minutes)
    private getCacheDuration(): number {
        const sessionLifetime = 15 * 60 * 1000; // 15 minutes
        return Math.min(sessionLifetime, this.DEFAULT_CACHE_DURATION);
    }

    set(channelName: string, authData: unknown): void {
        const now = Date.now();
        const cacheDuration = this.getCacheDuration();

        this.cache.set(channelName, {
            authData,
            timestamp: now,
            expiresAt: now + cacheDuration,
            sessionId: this.currentSessionId || undefined
        });

    }

    get(channelName: string): unknown | null {
        const entry = this.cache.get(channelName);
        if (!entry) return null;

        // Check if session has changed
        if (entry.sessionId && entry.sessionId !== this.currentSessionId) {
            this.cache.delete(channelName);
            // Session changed, entry removed
            return null;
        }

        // Check if expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(channelName);
            // Cache entry expired
            return null;
        }

        // Cache hit
        return entry.authData;
    }

    clear(): void {
        this.cache.clear();
    }

    // Clean expired entries and entries from different sessions
    cleanup(): void {
        const now = Date.now();

        for (const [channelName, entry] of this.cache.entries()) {
            const isExpired = now > entry.expiresAt;
            const isFromDifferentSession = entry.sessionId && entry.sessionId !== this.currentSessionId;

            if (isExpired || isFromDifferentSession) {
                this.cache.delete(channelName);
            }
        }

    }

    // Get cache statistics for debugging
    getStats(): { size: number; currentSessionId: string | null; oldestEntry: number | null } {
        const now = Date.now();
        let oldestTimestamp: number | null = null;

        for (const entry of this.cache.values()) {
            if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
                oldestTimestamp = entry.timestamp;
            }
        }

        return {
            size: this.cache.size,
            currentSessionId: this.currentSessionId,
            oldestEntry: oldestTimestamp ? Math.round((now - oldestTimestamp) / 1000) : null
        };
    }

    // Cleanup on destruction
    destroy(): void {
        if (this.sessionCheckTimer) {
            clearInterval(this.sessionCheckTimer);
            this.sessionCheckTimer = null;
        }
        this.clear();
    }
}

const authCache = new AuthCache();

// Cleanup expired cache entries every 2 minutes
setInterval(() => authCache.cleanup(), 2 * 60 * 1000);

// Export auth cache for external cleanup (e.g., on logout)
export const clearAuthCache = () => authCache.clear();

// Export cache stats for debugging and monitoring
export const getAuthCacheStats = () => authCache.getStats();

// Export cache cleanup for manual cleanup
export const cleanupAuthCache = () => authCache.cleanup();

// Function to create and export the Echo instance
export default (async () => {
    // Ensure CSRF token is available before initializing Echo
    await ensureCsrfToken();

    const csrfToken = getCookieValue('XSRF-TOKEN');

    // API base is configured in ApiClient (VITE_API_URL). We'll call the API-relative endpoint

    // Log the URL being used for broadcasting auth

    // Função para verificar se o usuário está autenticado antes de inicializar o Echo
    const checkUserAuthentication = async () => {
        try {
            const response = await ApiClient.get('/auth/me');
            return response.data;
        } catch (error) {
            console.error('[Echo] User is not authenticated:', error);
            return false;
        }
    };

    // Verificar autenticação antes de inicializar o Echo
    const authUser = await checkUserAuthentication();
    if (!authUser) {
        console.warn('[Echo] User is not authenticated. Private channels will not work.');
    }

    // Garantir que temos um token CSRF válido antes de inicializar o Echo
    if (!csrfToken) {
        await ensureCsrfToken();
    }

    // Obter o token CSRF atualizado
    const updatedCsrfToken = getCookieValue('XSRF-TOKEN');

    const echo = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: parseInt(import.meta.env.VITE_REVERB_PORT || '443'),
        wssPort: parseInt(import.meta.env.VITE_REVERB_PORT || '443'),
        forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
        enabledTransports: ['ws', 'wss'],
        disableStats: true, // Disable stats to reduce network traffic

        authorizer: (channel: { name: string }, _options: unknown) => { // eslint-disable-line @typescript-eslint/no-unused-vars
            return {
                authorize: async (socketId: string, callback: (error: Error | null, authInfo: unknown) => void) => {

                    const cachedAuthData = authCache.get(channel.name);
                    if (cachedAuthData) {
                        callback(null, cachedAuthData);
                        return;
                    }

                    try {
                        const response = await ApiClient.post(
                            '/broadcasting/auth',
                            {
                                socket_id: socketId,
                                channel_name: channel.name,
                            },
                            {
                                withCredentials: true,
                                headers: updatedCsrfToken ? { 'X-XSRF-TOKEN': updatedCsrfToken } : undefined,
                            }
                        );

                        // Auth request successful

                        authCache.set(channel.name, response.data);
                        callback(null, response.data);
                    } catch (error: unknown) {
                        // Type guard for AxiosError
                        let status: number | undefined;
                        let errorMessage = `Authorization failed for ${channel.name}`;
                        if (axios.isAxiosError(error)) {
                            status = error.response?.status;
                            errorMessage = `Authorization failed for ${channel.name}: ${status}`;
                            console.error(
                                '[Echo Custom Authorizer] Auth request failed (AxiosError):',
                                status,
                                error.response?.data,
                                error
                            );
                        } else {
                            console.error(
                                '[Echo Custom Authorizer] Auth request failed (UnknownError):',
                                error
                            );
                        }
                        callback(new Error(errorMessage), null);
                    }
                },
            };
        },
        debug: true, // Enable debug mode for troubleshooting
    });

    // Define a more specific type for Pusher errors if possible
    interface PusherError {
        type: string;
        error?: {
            type: string;
            data: {
                code: number;
                message: string;
                status?: number; // Adding status here if it's part of error.data
            };
        };
        status?: number; // If status is a top-level property
        data?: unknown; // Fallback for other data structures, changed from any to unknown
    }

    // Add error handling for Echo
    echo.connector.pusher.connection.bind('error', async (error: unknown) => {
        console.error('[Echo] Connection error:', error);
        // Attempt to access properties assuming it might be a PusherError-like object
        const pusherError = error as PusherError;

        if (pusherError && pusherError.type === 'AuthError') {
            console.error('[Echo] Authentication error details:', {
                type: pusherError.type,
                status: pusherError.status || pusherError.error?.data?.status, // Access status more safely
                data: pusherError.data || pusherError.error?.data, // Access data more safely
                csrfToken: updatedCsrfToken ? `${updatedCsrfToken.substring(0, 10)}...` : 'null',
                cookiesPresent: document.cookie.split(';').map(c => c.trim().split('=')[0]),
                authEndpoint: `/api/broadcasting/auth`,
            });

            // Tentar obter um novo token CSRF e reconectar
            await ensureCsrfToken();

            // Headers são gerenciados pelo custom authorizer

            echo.connector.pusher.connection.reconnect();
        }
    });

    echo.connector.pusher.connection.bind('connected', () => {
        // Connection established
    });

    // Add a custom method to check authentication status
    echo.checkAuth = async () => {
        try {
            // Try to fetch the current user to verify authentication
            const response = await ApiClient.get('/auth/me');
            return response.data;
        } catch {
            console.error('[Echo] Authentication check failed');

            // Tentar obter um novo token CSRF
            await ensureCsrfToken();

            // Tentar novamente após obter um novo token
            try {
                const retryResponse = await ApiClient.get('/auth/me');
                return retryResponse.data;
            } catch (retryError) {
                console.error('[Echo] Authentication retry failed even after fetching new CSRF token:', retryError);
                return false;
            }
        }
    };

    window.Echo = echo;
    return echo;
})();
